import { useState } from 'react';
import { Dialog, DialogContent, Grid, IconButton, Typography, SxProps, Theme, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
import { Syndicate } from '../models/syndicate';
import { PoolXNumberFormat } from '../common/CurrencyFormatter';
import { Button } from '../reusable-components';
import { operatorAdviceErrorOccurred, operatorAdviceBetSuccess, operatorAdvicePurchase, operatorAdviceBetInitiated } from "../App";
import dbet_questionmark from '../images/dbet_questionmark.png';

interface SyndicateConfirmDialogProps {
    open: boolean;
    onClose: () => void;
    syndicate: Syndicate;
    purchase: any;
    parentOnClose?: () => void;
}

export default function SyndicateConfirmDialog({ open, onClose, syndicate, purchase, parentOnClose }: SyndicateConfirmDialogProps) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [confirmationState, setConfirmationState] = useState<'confirm' | 'success' | 'error'>('confirm');
    const [errorMessage, setErrorMessage] = useState<string>('');

    let locale = t('locale');
    const nf = PoolXNumberFormat(locale, {
        style: 'currency',
        currency: syndicate.totalAmountPerShare.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    const phrases = {
        confirmPurchase: t('do_you_want_to_place_bet'),
        pay: t('pay_syndicate'),
        paymentDone: t('your_bet_is_paid'),
        insufficientFunds: t('insufficient_funds'),
        gamingLimitsReached: t('gaming_limits_reached'),
        unableToCompletePurchase: t('unable_to_complete_purchase'),
    };

    const handleConfirm = async () => {
        setLoading(true);
        operatorAdvicePurchase({
            type: "purchase",
            ticketName: syndicate.name,
            amount: String(syndicate.totalAmountPerShare.amount),
            currency: syndicate.totalAmountPerShare.currency,
            status: "pending",
        });
        operatorAdviceBetInitiated({
            type: "bet_initiated",
            syndicateId: syndicate.id,
            roundId: syndicate.roundId,
            amount: String(syndicate.totalAmountPerShare.amount),
            currency: syndicate.totalAmountPerShare.currency,
        }, -1);
        try {
            const resp = await purchase();
            const j = await resp.json();

            if (j.errorCode !== undefined) {
                let errorText = phrases.unableToCompletePurchase;

                if (j.localizedOperatorMessage !== undefined && j.localizedOperatorMessage !== '') {
                    errorText = j.localizedOperatorMessage
                }

                switch (j.errorCode) {
                    case 101:
                        errorText = phrases.unableToCompletePurchase;
                        break;
                    case 102:
                        errorText = phrases.insufficientFunds;
                        break;
                    case 105:
                        errorText = phrases.gamingLimitsReached;
                        break;
                    default:
                        break;
                }

                operatorAdviceErrorOccurred({
                    errorCode: j.errorCode,
                    errorMessage: errorText,
                    operatorErrorCode: j.operatorErrorCode,
                })
                setErrorMessage(errorText);
                setConfirmationState('error');
            } else {
                if (j && j.couponShareId) {
                    operatorAdviceBetSuccess({
                        type: "bet_success",
                        amount: j.totalStake?.amount,
                        currency: j.totalStake?.currency,
                        transactionId: j.transactionId,
                        roundId: j.roundId,
                    });
                }
                setConfirmationState('success');
            }
        } catch (error) {
            console.error('Purchase error:', error);
            setErrorMessage(phrases.unableToCompletePurchase);
            setConfirmationState('error');
        }
        setLoading(false);
    }

    const handleClose = () => {
        setConfirmationState('confirm');
        setErrorMessage('');
        onClose();
        parentOnClose?.();
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    background: 'var(--background_1)',
                    width: '400px',
                }
            }}
        >
            <Grid container sx={{
                height: '50px',
                position: 'relative',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: '8px'
            }}>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        color: 'var(--text-black)',
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Grid>

            <DialogContent sx={{ padding: '20px', overflow: 'auto', maxHeight: 'calc(90vh - 50px)' }}>
                <Grid container direction="column" spacing={2} alignItems="center">
                    {/* Icon */}
                    <Grid item>
                        <Box
                            component="img"
                            src={dbet_questionmark}
                            alt="Confirmation"
                            sx={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'contain'
                            }}
                        />
                    </Grid>

                    {/* Message */}
                    <Grid item xs={12}>
                        {confirmationState === 'confirm' && (
                            <>
                                <Typography sx={{
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: 'var(--text-black)',
                                    textAlign: 'center',
                                    marginBottom: '10px'
                                }}>
                                    {phrases.confirmPurchase}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '24px',
                                    fontWeight: '700',
                                    color: '#4075FF',
                                    textAlign: 'center'
                                }}>
                                    {nf.format(syndicate.totalAmountPerShare.amount)}
                                </Typography>
                            </>
                        )}

                        {confirmationState === 'success' && (
                            <Typography sx={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: 'var(--text-black)',
                                textAlign: 'center'
                            }}>
                                {phrases.paymentDone}
                            </Typography>
                        )}

                        {confirmationState === 'error' && (
                            <Typography sx={{
                                fontSize: '18px',
                                fontWeight: '600',
                                color: '#FF0000',
                                textAlign: 'center'
                            }}>
                                {errorMessage || phrases.unableToCompletePurchase}
                            </Typography>
                        )}
                    </Grid>

                    {/* Buttons */}
                    <Grid item xs={12} sx={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
                        {confirmationState === 'confirm' ? (
                            <>
                                <Button
                                    style={{ minWidth: '120px' }}
                                    color="#FFFFFF"
                                    hoverColor="#FFFFFF"
                                    textColor="#4075FF"
                                    borderRadius="52px"
                                    onClick={handleClose}
                                    disabled={loading}
                                    border="2px solid #4075FF"
                                >
                                    {t('cancel')}
                                </Button>
                                <Button
                                    style={{ minWidth: '120px' }}
                                    color="#4075FF"
                                    hoverColor="#4075FF"
                                    borderRadius="52px"
                                    onClick={handleConfirm}
                                    disabled={loading}
                                >
                                    {phrases.pay}
                                </Button>
                            </>
                        ) : (
                            <Button
                                style={{ minWidth: '120px' }}
                                color="#4075FF"
                                hoverColor="#4075FF"
                                borderRadius="52px"
                                onClick={handleClose}
                            >
                                {t('ok')}
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
}
